/**
 * Theme Configuration
 * BizFirst Green Design System
 */

export const colors = {
  // Brand Gradients & Accents (Neon)
  primary: {
    50: '#f0faf1',
    100: '#dcf0de',
    200: '#b7debb',
    300: '#82c98a',
    400: '#3A8C45', // Green
    500: '#2d7038',
    600: '#256030',
    700: '#1e5028',
    800: '#174020',
    900: '#103018',
    glow: '0 0 20px rgba(58, 140, 69, 0.6)', // Green Glow
  },

  // Dark/Midnight Backgrounds
  background: {
    main: '#020617', // Slate 950 (Deep Midnight)
    surface: 'rgba(15, 23, 42, 0.6)', // Glassy Midnight
    overlay: 'rgba(2, 6, 23, 0.8)',
    glass: 'rgba(255, 255, 255, 0.03)',
    glassHover: 'rgba(58, 140, 69, 0.1)', // Cyan tint on hover
  },

  // Semantic (Neon High Contrast)
  success: {
    main: '#10b981', // Emerald
    glow: '0 0 15px rgba(16, 185, 129, 0.5)',
    bg: 'rgba(16, 185, 129, 0.15)',
  },
  error: {
    main: '#f43f5e', // Rose
    glow: '0 0 15px rgba(244, 63, 94, 0.5)',
    bg: 'rgba(244, 63, 94, 0.15)',
  },
  warning: {
    main: '#f59e0b', // Amber
    glow: '0 0 15px rgba(245, 158, 11, 0.5)',
    bg: 'rgba(245, 158, 11, 0.15)',
  },

  // Text
  text: {
    primary: '#f8fafc', // Slate 50
    secondary: '#94a3b8', // Slate 400
    muted: '#64748b', // Slate 500
    onPrimary: '#ffffff', // White text on green button
  },

  // Node Category Colors (Muted Deep Gradients)
  trigger: { from: '#1a4d2a', to: '#0d2516' },    // Deep forest green
  http: { from: '#2e1f5c', to: '#1a1238' },        // Deep muted violet
  database: { from: '#4a3210', to: '#2a1c08' },    // Deep amber/bronze
  transform: { from: '#0d3d2e', to: '#071e18' },   // Deep teal
  logic: { from: '#1f2060', to: '#12133a' },       // Deep indigo
  notification: { from: '#4a1a2e', to: '#2a0e1a' },// Deep rose
  file: { from: '#17305c', to: '#0d1c38' },        // Deep blue
  blockchain: { from: '#2d1a52', to: '#180e30' },  // Deep purple
  'ai-agent': { from: '#1a4d2a', to: '#0d2516' },  // Deep green (brand)
  llm: { from: '#2d1a52', to: '#180e30' },         // Deep purple
  tool: { from: '#0d3d2e', to: '#071e18' },        // Deep emerald
  'ai-memory': { from: '#3d0d45', to: '#220728' }, // Deep fuchsia
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
};

export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

export const fontSize = {
  xs: '11px',
  sm: '13px',
  base: '14px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
};

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
  glow: '0 0 20px rgba(58, 140, 69, 0.4)',
  glass: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
};

export const theme = {
  colors,
  spacing,
  borderRadius,
  fontSize,
  fontWeight,
  shadows,
};

export type DesignerTheme = typeof theme;
